import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Smartphone, Lightbulb, Beaker, Package, ShoppingCart, Target, Rocket } from 'lucide-react';
import { ProductReleaseModal } from './ProductReleaseModal';
import { DiscontinueConfirmationModal } from './dashboard/modals/DiscontinueConfirmationModal';
import { Modal } from '../ui/Modal';
import { reviewGenerator } from '@/services/tycoon/reviewGeneratorService';
import type { Product, ProductReviews } from '@/types/product';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
interface ProductPipelineProps {
  products: Product[];
  onProductAction: (productId: string, action: string) => void;
  companyName?: string;
  getPhaseProgress?: (product: Product) => { progress: number; daysRemaining: number; totalDays: number };
  releaseModalOpen?: boolean;
  productToRelease?: Product | null;
  productReviews?: ProductReviews;
  onReleaseModalChange?: (open: boolean, product: Product | null, reviews: ProductReviews) => void;
  discontinueModalOpen?: boolean;
  productToDiscontinue?: Product | null;
  onDiscontinueModalChange?: (open: boolean, product: Product | null) => void;
  productAllocations?: Array<{
    productId: string;
    productName: string;
    allocations: Array<{
      phase: string;
      employeeType: string;
      count: number;
    }>;
    totalEmployees: number;
  }>;
  isProductStagePaused?: (productId: string) => boolean;
}

interface PreferenceInfo {
  preference: string;
  label: string;
  color: string;
}

export function ProductPipeline({
  products,
  onProductAction,
  companyName,
  getPhaseProgress,
  releaseModalOpen = false,
  productToRelease = null,
  productReviews = { professional: [], customer: [] },
  onReleaseModalChange,
  discontinueModalOpen = false,
  productToDiscontinue = null,
  onDiscontinueModalChange,
  productAllocations = [],
  isProductStagePaused
}: ProductPipelineProps) {
  const navigate = useNavigate();
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [productPreferences, setProductPreferences] = useState<Record<string, PreferenceInfo | null>>({});

  const startNewProduct = () => {
    setShowNewProduct(false);
    navigate('/designer', { 
      state: { 
        fromTycoon: true,
        companyName: companyName,
        productCount: products.length
      }
    });
  };

  // Create a stable key based on product IDs and statuses to prevent unnecessary re-renders
  const productKey = useMemo(() => {
    return products
      .filter(p => ['design', 'engineering', 'testing', 'manufacturing'].includes(p.status))
      .map(p => `${p.id}-${p.status}`)
      .join(',');
  }, [products]);

  // Fetch product preference information using simple preference system
  useEffect(() => {
    if (!productKey) {
      setProductPreferences({});
      return;
    }

    const fetchPreferences = async () => {
        const { eventEngine } = await import('@/services/tycoon/eventEngine');
      const newPreferences: Record<string, PreferenceInfo | null> = {};
      
      products.forEach(product => {
        if (product.status === 'design' || product.status === 'engineering' || 
            product.status === 'testing' || product.status === 'manufacturing') {
          const info = eventEngine.getProductPreferenceInfo(product.id, product);
          newPreferences[product.id] = info;
        }
      });
      
      setProductPreferences(newPreferences);
    };

    fetchPreferences();
  }, [productKey, products]);

  // Helper function to get employee allocation for a product
  const getProductEmployeeInfo = (productId: string) => {
    const allocation = productAllocations.find(a => a.productId === productId);
    return allocation;
  };

  // Helper function to render employee allocation info
  const renderEmployeeAllocation = (product: Product) => {
    const roleDisplayNames: Record<string, string> = {
      designer: 'Designers',
      engineer: 'Engineers',
      tester: 'Testers',
      assemblyWorker: 'Assembly Workers'
    };

    // Check if product has development phase with allocated employees
    if (product.developmentPhase?.allocatedEmployees) {
      const allocations = product.developmentPhase.allocatedEmployees;
      const isPaused = isProductStagePaused && isProductStagePaused(product.id);
      
      return (
        <div className={`mt-2 text-xs space-y-1 ${isPaused ? 'text-theme-error' : 'text-theme-text-secondary'}`}>
          {Object.entries(allocations).map(([employeeType, count]) => (
            <div key={employeeType} className="flex justify-between items-center">
              <span>{roleDisplayNames[employeeType] || employeeType}:</span>
              <span className={`font-medium ${isPaused ? 'text-theme-error' : ''}`}>
                {count as number} {isPaused && '(Paused)'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    
    // Fallback to productAllocations prop for backward compatibility
    const allocation = getProductEmployeeInfo(product.id);
    if (!allocation || !allocation.allocations.length) return null;

    const currentPhaseAllocations = allocation.allocations.filter(
      a => a.phase === product.status
    );

    if (currentPhaseAllocations.length === 0) return null;

    const isPaused = isProductStagePaused && isProductStagePaused(product.id);

    return (
      <div className={`mt-2 text-xs space-y-1 ${isPaused ? 'text-theme-error' : 'text-theme-text-secondary'}`}>
        {currentPhaseAllocations.map((allocation, index) => (
          <div key={index} className="flex justify-between items-center">
            <span>{roleDisplayNames[allocation.employeeType] || allocation.employeeType}:</span>
            <span className={`font-medium ${isPaused ? 'text-theme-error' : ''}`}>
              {allocation.count} {isPaused && '(Paused)'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderPreferenceIndicator = (product: Product) => {
    const preferenceInfo = productPreferences[product.id];
    if (!preferenceInfo) return null;

    return (
      <div className="mt-2">
        <div className="flex items-center gap-1 mb-1">
          <Target className="w-3 h-3 text-theme-text-muted" />
          <span className="text-xs text-theme-text-muted">Choice:</span>
        </div>
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${preferenceInfo.color}`}>
          <span>{preferenceInfo.label}</span>
        </div>
      </div>
    );
  };

  const renderProgressBar = (product: Product) => {
    if (!getPhaseProgress || !product.developmentPhase) return null;
    
    const { progress, daysRemaining } = getPhaseProgress(product);
    
    return (
      <div className="mt-2">
        <div className="flex justify-between text-xs text-theme-text-muted mb-1">
          <span>Progress</span>
          <span>{daysRemaining}d left</span>
        </div>
        <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
          <div
            className="bg-theme-accent h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const handleReleaseClick = (product: Product) => {
    const reviews = reviewGenerator.generateReviews(product);
    if (onReleaseModalChange) {
      onReleaseModalChange(true, product, reviews);
    }
  };

  const handleConfirmRelease = () => {
    if (productToRelease) {
      onProductAction(productToRelease.id, 'release');
      // Don't close the modal here - let ProductReleaseModal handle it
    }
  };

  const handleCloseReleaseModal = () => {
    if (onReleaseModalChange) {
      onReleaseModalChange(false, null, { professional: [], customer: [] });
    }
  };

  const handleCloseDiscontinueModal = () => {
    if (onDiscontinueModalChange) {
      onDiscontinueModalChange(false, null);
    }
  };

  const handleConfirmDiscontinue = () => {
    if (productToDiscontinue) {
      onProductAction(productToDiscontinue.id, 'discontinue-confirmed');
      handleCloseDiscontinueModal();
    }
  };

  return (
    <div className="space-y-6">

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-theme-text-primary">Product Pipeline</h2>
          <p className="text-sm text-theme-text-muted">Manage your product development and production</p>
        </div>
        <button
          onClick={() => onProductAction('new', 'develop')}
          className="flex items-center gap-2 px-4 py-2 bg-theme-accent text-white rounded-lg hover:bg-theme-accent transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Product
        </button>
      </div>

      {/* Vertical Pipeline Layout */}
      <div className="grid grid-cols-5 gap-4">
        {/* Stage 1: Design */}
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-theme-warning" />
            Design
          </h3>
          <div className="space-y-4">
            {products
              .filter(p => p.status === 'design')
              .map(product => (
                <div key={product.id} data-testid={`product-${product.name}`} className="border rounded-lg p-3">
                  <div className="mb-2">
                    <h4 className="font-medium text-theme-text-primary text-sm">{product.name}</h4>
                    <p className="text-xs text-theme-text-muted truncate">{product.description}</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Tech:</span>
                      <span className="font-medium">{product.techScore}</span>
                    </div>
                    {/*
                    {product.developmentPhase && (
                      <div className="flex justify-between text-xs">
                        <span className="text-theme-text-muted">Progress:</span>
                        <span className="font-medium">{product.developmentPhase.confidence}%</span>
                      </div>
                    )}
                    */}
                  </div>
                  {renderEmployeeAllocation(product)}
                  {renderPreferenceIndicator(product)}
                  {renderProgressBar(product)}
                </div>
              ))}
            {products.filter(p => p.status === 'design').length === 0 && (
              <div className="text-center py-8 text-theme-text-muted">
                <div className="text-sm">No products</div>
                <div className="text-xs">in design phase</div>
              </div>
            )}
          </div>
        </div>

        {/* Stage 2: Engineering */}
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary flex items-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-theme-accent" />
            Engineering
          </h3>
          <div className="space-y-4">
            {products
              .filter(p => p.status === 'engineering')
              .map(product => (
                <div key={product.id} data-testid={`product-${product.name}`} className="border rounded-lg p-3">
                  <div className="mb-2">
                    <h4 className="font-medium text-theme-text-primary text-sm">{product.name}</h4>
                    <p className="text-xs text-theme-text-muted truncate">{product.description}</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Tech:</span>
                      <span className="font-medium">{product.techScore}</span>
                    </div>
                    {product.developmentPhase && (
                      <div className="flex justify-between text-xs">
                        <span className="text-theme-text-muted">Progress:</span>
                        <span className="font-medium">{product.developmentPhase.confidence}%</span>
                      </div>
                    )}
                  </div>
                  {renderEmployeeAllocation(product)}
                  {renderPreferenceIndicator(product)}
                  {renderProgressBar(product)}
                </div>
              ))}
            {products.filter(p => p.status === 'engineering').length === 0 && (
              <div className="text-center py-8 text-theme-text-muted">
                <div className="text-sm">No products</div>
                <div className="text-xs">in engineering phase</div>
              </div>
            )}
          </div>
        </div>

        {/* Stage 3: Testing */}
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary flex items-center gap-2 mb-4">
            <Beaker className="w-5 h-5 text-theme-info" />
            Testing
          </h3>
          <div className="space-y-4">
            {products
              .filter(p => p.status === 'testing')
              .map(product => (
                <div key={product.id} data-testid={`product-${product.name}`} className="border rounded-lg p-3">
                  <div className="mb-2">
                    <h4 className="font-medium text-theme-text-primary text-sm">{product.name}</h4>
                    <p className="text-xs text-theme-text-muted truncate">{product.description}</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Tech:</span>
                      <span className="font-medium">{product.techScore}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Appeal:</span>
                      <span className="font-medium">{product.marketAppeal}%</span>
                    </div>
                    {product.developmentPhase && (
                      <div className="flex justify-between text-xs">
                        <span className="text-theme-text-muted">Quality:</span>
                        <span className="font-medium">{product.developmentPhase.qualityScore}%</span>
                      </div>
                    )}
                  </div>
                  {renderEmployeeAllocation(product)}
                  {renderPreferenceIndicator(product)}
                  {renderProgressBar(product)}
                </div>
              ))}
            {products.filter(p => p.status === 'testing').length === 0 && (
              <div className="text-center py-8 text-theme-text-muted">
                <div className="text-sm">No products</div>
                <div className="text-xs">in testing</div>
              </div>
            )}
          </div>
        </div>

        {/* Stage 4: Manufacturing */}
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-theme-warning" />
            Manufacturing
          </h3>
          <div className="space-y-4">
            {products
              .filter(p => p.status === 'manufacturing')
              .map(product => (
                <div key={product.id} data-testid={`product-${product.name}`} className="border rounded-lg p-3">
                  <div className="mb-2">
                    <h4 className="font-medium text-theme-text-primary text-sm">{product.name}</h4>
                    <p className="text-xs text-theme-text-muted truncate">{product.description}</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Cost:</span>
                      <span className="font-medium">{formatCurrency(product.productionCost || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Price:</span>
                      <span className="font-medium">{formatCurrency(product.price || 0)}</span>
                    </div>
                  </div>
                  {renderEmployeeAllocation(product)}
                  {renderPreferenceIndicator(product)}
                  {renderProgressBar(product)}
                </div>
              ))}
            {products.filter(p => p.status === 'manufacturing').length === 0 && (
              <div className="text-center py-8 text-theme-text-muted">
                <div className="text-sm">No products</div>
                <div className="text-xs">in manufacturing</div>
              </div>
            )}
          </div>
        </div>

        {/* Stage 5: Production */}
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary flex items-center gap-2 mb-4">
            <ShoppingCart className="w-5 h-5 text-theme-success" />
            Production
          </h3>
          <div className="space-y-4">
            {products
              .filter(p => p.status === 'production' || p.status === 'released')
              .map(product => (
                <div key={product.id} data-testid={`product-${product.name}`} className="border rounded-lg p-3">
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-theme-text-primary text-sm">{product.name}</h4>
                      {product.status === 'released' && (
                        <span className="text-xs bg-theme-success-light text-theme-success-dark px-2 py-1 rounded">
                          Released
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-theme-text-muted truncate">{product.description}</p>
                  </div>
                  {product.status === 'released' ? (
                    <button
                      onClick={() => onDiscontinueModalChange?.(true, product)}
                      data-testid="discontinue-product"
                      className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-theme-error-light text-theme-error rounded text-xs hover:opacity-80"
                    >
                      Discontinue
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReleaseClick(product)}
                      data-testid="release-product"
                      className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-theme-info-light text-theme-info rounded text-xs hover:opacity-80"
                    >
                      <Rocket className="w-3 h-3" />
                      Release
                    </button>
                  )}
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Price:</span>
                      <span className="font-medium">{formatCurrency(product.price || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Appeal:</span>
                      <span className="font-medium">{product.marketAppeal}%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-theme-text-muted">Margin:</span>
                      <span className={`font-medium ${
                        product.price > product.productionCost * 1.5 
                          ? 'text-theme-success'
                          : 'text-theme-error'
                      }`}>
                        {product.price && product.productionCost 
                          ? Math.round((product.price - product.productionCost) / product.price * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            {products.filter(p => p.status === 'production' || p.status === 'released').length === 0 && (
              <div className="text-center py-8 text-theme-text-muted">
                <div className="text-sm">No products</div>
                <div className="text-xs">in production</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showNewProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-theme-bg-content p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Start New Product Development</h3>
            <div className="space-y-4">
              <button
                onClick={startNewProduct}
                className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-theme-bg-secondary transition-colors"
              >
                <Smartphone className="w-6 h-6 text-theme-accent" />
                <div className="text-left">
                  <h4 className="font-medium">Design New Phone</h4>
                  <p className="text-sm text-theme-text-muted">
                    Create a new phone design from scratch
                  </p>
                </div>
              </button>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowNewProduct(false)}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProductReleaseModal
        isOpen={releaseModalOpen}
        onClose={handleCloseReleaseModal}
        product={productToRelease}
        reviews={productReviews}
        onConfirmRelease={handleConfirmRelease}
      />

      <Modal
        isOpen={discontinueModalOpen}
        onClose={handleCloseDiscontinueModal}
        size="lg"
      >
        {productToDiscontinue && (
          <DiscontinueConfirmationModal
            product={productToDiscontinue}
            onConfirm={handleConfirmDiscontinue}
            onCancel={handleCloseDiscontinueModal}
          />
        )}
      </Modal>
    </div>
  );
}

export default ProductPipeline;
